import type { FunnelStageKey, InvStatus } from './types';

export const models = ['ChatGPT', 'Claude', 'Gemini', 'Perplexity'];
export const modelColors: Record<string, string> = {
  ChatGPT: '#12b76a',
  Claude: '#28a9d2',
  Gemini: '#7e5fe0',
  Perplexity: '#f93',
};

export const globalAnalysis = {
  finding: 'Claude is near ceiling on Broad Approval (0.828) — the only deeply colored cell. That variable is positioning Sunbit as a backup. More encoding here won\'t help. The six lightly colored variables are where the blueprint targets.',
  quotes: [
    { text: '"Excellent for higher approval rates (~85%)" — Claude Q1. No mention of practice workflow, fees, or case acceptance in the same response.', type: 'neutral' as const },
  ],
  watch: { label: 'Watch: Gemini Inversion', text: 'Gemini Practice Workflow at 0.048, nearly invisible. Explains why Gemini names Sunbit first but stops short of recommending it.' },
};

// Model name mappings between API keys and display names
export const MODEL_DISPLAY: Record<string, string> = {
  openai: 'ChatGPT',
  claude: 'Claude',
  gemini: 'Gemini',
  perplexity: 'Perplexity',
};
export const MODEL_API_KEY: Record<string, string> = {
  ChatGPT: 'openai',
  Claude: 'claude',
  Gemini: 'gemini',
  Perplexity: 'perplexity',
};
export const MODEL_ORDER = ['ChatGPT', 'Claude', 'Gemini', 'Perplexity'];
export const DOT_COLORS: Record<string, string> = {
  ChatGPT: '#12b76a', Claude: '#28a9d2', Gemini: '#7e5fe0', Perplexity: '#f93',
};

// The scoring rubric is 0/1/2 per response, so an averaged variable score maxes
// at 2.0. Bars and the 0-100 display normalization divide by this true max.
export const CHART_MAX = 2.0;

export const VARIABLE_MAX = 2.0;

export const personaMetricCols = [
  { key: 'mention',    label: 'Mention Rate' },
  { key: 'top5',      label: 'Top-5 Rate' },
  { key: 'primaryRec', label: 'Primary Rec' },
  { key: 'bestModel', label: 'Best Model' },
] as const;

export const FUNNEL_STAGES: { key: FunnelStageKey; label: string }[] = [
  { key: 'mentionRate', label: 'mention rate' },
  { key: 'top5Rate', label: 'top-5 rate' },
  { key: 'primaryRecRate', label: 'primary recommendation' },
];

// ─── What AI Says — Smart Inbox ──────────────────────────────────────────────

export const INBOX_RAIL_MIN = 300;
export const INBOX_RAIL_MAX = 580;
export const INBOX_RAIL_DEFAULT = 420;

export const inboxModelColors: Record<string, { color: string; bg: string; border: string }> = {
  ChatGPT:    { color: '#12b76a', bg: 'rgba(18,183,106,0.10)',  border: 'rgba(18,183,106,0.25)'  },
  Claude:     { color: '#28a9d2', bg: 'rgba(40,169,210,0.10)',  border: 'rgba(40,169,210,0.25)'  },
  Gemini:     { color: '#7e5fe0', bg: 'rgba(126,95,224,0.10)', border: 'rgba(126,95,224,0.25)'  },
  Perplexity: { color: '#f93',    bg: 'rgba(255,153,51,0.10)',  border: 'rgba(255,153,51,0.25)'  },
};

// ─── Blueprint helpers ────────────────────────────────────────────────────────

export const WAVE_ACCENT: Record<number, { color: string; bg: string; border: string }> = {
  1: { color: '#00c9c4', bg: 'rgba(0,201,196,0.08)', border: 'rgba(0,201,196,0.22)' },
  2: { color: '#28a9d2', bg: 'rgba(40,169,210,0.08)', border: 'rgba(40,169,210,0.22)' },
  3: { color: '#7e5fe0', bg: 'rgba(126,95,224,0.08)', border: 'rgba(126,95,224,0.22)' },
};

export const EFFORT_STYLE: Record<string, { bg: string; color: string }> = {
  LOW:    { bg: 'rgba(18,183,106,0.14)', color: '#12b76a' },
  MEDIUM: { bg: 'rgba(247,144,9,0.14)', color: '#f79009' },
  HIGH:   { bg: 'rgba(240,68,56,0.14)', color: '#f04438' },
};

export const STATUS_LABEL: Record<InvStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete:    'Complete',
};

export const STATUS_STYLE: Record<InvStatus, { bg: string; color: string; border: string }> = {
  not_started: { bg: 'var(--muted)',  color: 'var(--muted-foreground)', border: 'var(--border)' },
  in_progress: { bg: 'rgba(247,144,9,0.14)', color: '#f79009', border: '#f79009' },
  complete:    { bg: 'rgba(18,183,106,0.14)', color: '#12b76a', border: '#12b76a' },
};

export const STATUS_CYCLE: InvStatus[] = ['not_started', 'in_progress', 'complete'];

// ─── Key Insights ──────────────────────────────────────────────────────────

export const deltaColors: Record<string, { text: string; border: string }> = {
  positive: { text: '#12b76a', border: '#12b76a' },
  negative: { text: '#ff6d66', border: '#ff6d66' },
  neutral:  { text: '#28a9d2', border: '#28a9d2' },
  info:     { text: '#7e5fe0', border: '#7e5fe0' },
};

// ── _INSIGHT_STYLE — used by buildInsightCardsFromApi fallback styling ──
export const _INSIGHT_STYLE: Record<number, { type: string; typeColor: string; typeBg: string; accentColor: string }> = {
  2: { type: 'Strong signal',   typeColor: '#15B79E', typeBg: '#F0FDFB', accentColor: '#15B79E' },
  1: { type: 'Partial signal',  typeColor: '#0EA5E9', typeBg: '#F0F9FF', accentColor: '#0EA5E9' },
  0: { type: 'Not indexed',     typeColor: '#717680', typeBg: '#F7F8FA', accentColor: '#b2b5b9' },
};

// ─── Funnel Card Definitions ─────────────────────────────────────────────────

export const FUNNEL_CARD_DEFS = [
  {
    sub: 'Mention Rate',
    definition: 'The share of AI responses that name your brand at least once. This is the top of the funnel. It shows whether AI models know your brand exists in this category.',
    calculation: 'Responses where your brand appears by name divided by total responses scored, averaged across the AI models measured in this run.',
    interpretation: 'A high mention rate means broad AI awareness. If this is high but Primary Rec is low, the gap lives further down the funnel and is an encoding problem, not an awareness problem.',
    goodRange: '70% or higher is strong. 50-69% is competitive. Below 50% means the brand is not consistently surfaced and awareness-level content investment is the priority.',
    aiQuestion: 'My Mention Rate is {value}. What does that tell you about my brand\'s AI visibility and what should I focus on first?',
  },
  {
    sub: 'Top-5 Rate',
    definition: 'The share of responses where your brand appears in the top 5 named options. This measures whether you make the AI consideration set.',
    calculation: 'Responses where your brand is listed among the first 5 brands named, divided by total responses that include a ranked list, averaged across the AI models measured in this run.',
    interpretation: 'Being in the top 5 is the threshold for active consideration. A sharp drop from Mention Rate to Top-5 Rate means the brand is mentioned but not placed competitively. Models know you but do not rank you.',
    goodRange: '60% or higher is strong. 40-59% is competitive. Below 40% means you\'re being mentioned but not making the shortlist.',
    aiQuestion: 'My Top-5 Rate is {value}. What\'s the gap between that and my Mention Rate telling you, and what\'s driving the drop?',
  },
  {
    sub: '#1 on List',
    definition: 'The share of responses where your brand is the first brand named. This measures position in how a model lists options, not endorsement: a response can name you first and still recommend another brand as its pick.',
    calculation: 'Responses where your brand is the first brand named, divided by total responses that include a ranked list, averaged across the AI models measured in this run.',
    interpretation: 'Being named first shows a model reaches for your brand early when it lists options. It is a diagnostic cut, not the win condition: Primary Rec measures the explicit endorsement. Comparing the two per model shows whether a model that lists you first also commits to you when asked for one best answer.',
    goodRange: '25% or higher is strong. 10-24% is competitive. Below 10% means you rarely lead the list. Always read it next to Primary Rec: leading lists without winning the endorsement, or winning endorsements without leading lists, are different problems with different fixes.',
    aiQuestion: 'My #1 on List rate is {value}. How does it compare with my Primary Rec rate, and what does that gap say about how models list versus endorse my brand?',
  },
  {
    sub: 'Primary Rec',
    definition: 'The share of responses where your brand is the explicit primary recommendation: the one the AI endorses when asked for a single best answer.',
    calculation: 'Responses where your brand is identified as the primary recommendation (not just listed), divided by total responses scored, averaged across the AI models measured in this run.',
    interpretation: 'This is the most commercially meaningful metric. A large gap between Top-5 and Primary Rec means models know you but cannot justify endorsing you. That is an encoding problem, not a visibility problem.',
    goodRange: '20% or higher is strong. 10-19% is developing. Below 10% means models aren\'t confident enough to commit to a primary endorsement even when they list you.',
    aiQuestion: 'My Primary Recommendation rate is {value}. What is the biggest gap between where I am and what it would take to get above 20%?',
  },
];
